import React, { useEffect, useMemo, useRef, useState } from "react";
import Slider from "react-slick";
import { useDispatch, useSelector } from "react-redux";
import { fetchTopBanners } from "../../features/slices/GamesSlice";
import {
  trackBannerClick,
  trackBannerImpression,
} from "../../api/services/banners";

const DEFAULT_SLIDE_DURATION = 7000;

// an impression means the banner was actually seen, so it is only reported
// once the active slide has held still, on screen, in a foreground tab, for
// long enough to register - roughly the IAB viewability rule
const IMPRESSION_DWELL_MS = 1000;
const IMPRESSION_VISIBLE_RATIO = 0.5;
// every app modal/popup renders as `.modal.show`, mounted only while open
const OPEN_MODAL_SELECTOR = ".modal.show";

const isAnyModalOpen = () =>
  Boolean(document.querySelector(OPEN_MODAL_SELECTOR));

// a modal covers the carousel without moving it off screen, so the
// IntersectionObserver alone would still count banners behind its backdrop
function useIsModalOpen() {
  const [isModalOpen, setIsModalOpen] = useState(isAnyModalOpen);

  useEffect(() => {
    if (typeof MutationObserver === "undefined") return undefined;

    // modals are mounted/unmounted, so watching child lists is enough
    const observer = new MutationObserver(() =>
      setIsModalOpen(isAnyModalOpen())
    );
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  return isModalOpen;
}

function BannerSlide({ banner, isMuted, onMuteToggle, onBannerClick }) {
  const [hasError, setHasError] = useState(false);
  const clickable = Boolean(banner.hyperlink);
  // banners fetched through the API are normalised to a known animation, but a
  // banner reaching the carousel by any other path must still land on the fade
  // default rather than on a class no stylesheet defines
  const mediaClassName = `banner-animation-${banner.animation || "fade"}`;
  const frameClassName = [
    "banner-slide-frame",
    mediaClassName,
    banner.isVideo ? "video-slide" : "",
    clickable ? "slide-clickable" : "",
    hasError ? "banner-slide-placeholder" : "",
  ]
    .filter(Boolean)
    .join(" ");

  // picture-in-picture fill: a blurred, zoomed copy of the media covers the
  // frame so the media itself can be shown whole instead of being cropped
  const backdropUrl = banner.isVideo ? banner.thumbnailUrl : banner.mediaUrl;

  const handleClick = () => onBannerClick(banner);

  const wrapSlide = (content) => (
    <div className="banner-slide-shell">{content}</div>
  );

  const backdrop = backdropUrl ? (
    <img
      className="banner-slide-backdrop"
      src={backdropUrl}
      alt=""
      aria-hidden="true"
      loading="lazy"
    />
  ) : null;

  if (hasError || !banner.mediaUrl) {
    return wrapSlide(
      <div className={frameClassName} onClick={handleClick} aria-hidden="true" />
    );
  }

  if (banner.isVideo) {
    return wrapSlide(
      <div className={frameClassName} onClick={handleClick}>
        {backdrop}
        <video
          autoPlay
          loop
          muted={isMuted}
          playsInline
          className="banner-vdo banner-slide-media"
          onError={() => setHasError(true)}
        >
          <source src={banner.mediaUrl} type="video/mp4" />
        </video>
        <button
          type="button"
          className="banner-mute-toggle"
          aria-label={isMuted ? "Unmute banner video" : "Mute banner video"}
          onClick={onMuteToggle}
        >
          <i
            className={
              isMuted ? "bi bi-volume-mute-fill" : "bi bi-volume-up-fill"
            }
          />
        </button>
      </div>
    );
  }

  return wrapSlide(
    <div className={frameClassName} onClick={handleClick}>
      {backdrop}
      <img
        className="banner-slide-media"
        loading="lazy"
        src={banner.mediaUrl}
        alt=""
        title={banner.title}
        onError={() => setHasError(true)}
      />
    </div>
  );
}

export function Carousel() {
  const dispatch = useDispatch();
  const sliderRef = useRef(null);
  const containerRef = useRef(null);

  const game = useSelector((state) => state.game);
  const banners = useMemo(() => game?.banners || [], [game?.banners]);

  const [isMuted, setIsMuted] = useState(true);
  const [slideDuration, setSlideDuration] = useState(DEFAULT_SLIDE_DURATION);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isInView, setIsInView] = useState(false);
  const [isPageVisible, setIsPageVisible] = useState(
    () => document.visibilityState !== "hidden"
  );
  const isModalOpen = useIsModalOpen();

  // banners already counted for this page view - the carousel loops forever,
  // so without this every idle minute would report a fresh impression
  const reportedImpressions = useRef(new Set());

  useEffect(() => {
    if (!banners.length) {
      dispatch(fetchTopBanners());
    }
  }, [banners.length, dispatch]);

  useEffect(() => {
    if (banners.length) {
      setSlideDuration(banners[0].duration || DEFAULT_SLIDE_DURATION);
    }
  }, [banners]);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return undefined;

    if (typeof IntersectionObserver === "undefined") {
      setIsInView(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => setIsInView(entry.isIntersecting),
      { threshold: IMPRESSION_VISIBLE_RATIO }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  // autoplay keeps running in a background tab, so slides that advance while
  // the player is looking elsewhere must not be counted as seen
  useEffect(() => {
    const handleVisibilityChange = () =>
      setIsPageVisible(document.visibilityState !== "hidden");

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);

  // hold the carousel still while a modal covers it, so it doesn't rotate
  // unseen and resumes where the player left it
  useEffect(() => {
    const slick = sliderRef.current;
    if (!slick?.slickPause || !slick?.slickPlay || banners.length <= 1) return;
    if (isModalOpen) slick.slickPause();
    else slick.slickPlay();
  }, [isModalOpen, banners.length]);

  useEffect(() => {
    if (!isInView || !isPageVisible || isModalOpen) return undefined;

    const banner = banners[activeIndex];
    if (!banner?.id || reportedImpressions.current.has(banner.id)) {
      return undefined;
    }

    // the timer is cleared whenever the slide changes or the carousel leaves
    // the screen, so a slide swiped straight past never reports
    const timer = setTimeout(() => {
      reportedImpressions.current.add(banner.id);
      trackBannerImpression(banner.id);
    }, IMPRESSION_DWELL_MS);

    return () => clearTimeout(timer);
  }, [activeIndex, banners, isInView, isPageVisible, isModalOpen]);

  const handleBeforeChange = (_, newIndex) => {
    const banner = banners[newIndex];
    if (!banner) return;

    const nextDuration = banner.duration || DEFAULT_SLIDE_DURATION;
    setSlideDuration(nextDuration);

    const slick = sliderRef.current;
    if (slick?.slickPause && slick?.slickPlay) {
      slick.slickPause();
      slick.slickPlay();
    }
  };

  // centerMode leaves the neighbouring slides half visible, so the impression
  // follows the settled centre slide rather than anything merely on screen
  const handleAfterChange = (currentIndex) => setActiveIndex(currentIndex);

  const handleBannerClick = (banner) => {
    if (!banner?.hyperlink) return;
    // fire-and-forget: the hyperlink opens in a new tab, so this page stays
    // alive to finish the request and the click is never held up by it
    trackBannerClick(banner.id);
    window.open(banner.hyperlink, "_blank", "noopener,noreferrer");
  };

  const handleMuteToggle = (event) => {
    event.stopPropagation();
    setIsMuted((muted) => !muted);
  };

  const hasManyDots = banners.length > 10;
  // a lone banner has no neighbors to peek in, so centerMode's side padding
  // only pushes it off to one side - it spans the row and stays centered
  const isSingle = banners.length <= 1;
  // centerMode narrows the middle slide to peek its neighbours in at either
  // side, which only reads as a carousel once those two neighbours are
  // different banners. with two banners the track wraps onto itself, so the
  // same banner peeks in on the left and on the right while the active one
  // sits shrunken between the pair - the peek is only worth its side padding
  // from three banners up, below that the banner takes the full row
  const hasSidePeek = banners.length > 2;

  const settings = {
    className: "center",
    infinite: !isSingle,
    centerMode: hasSidePeek,
    centerPadding: hasSidePeek ? "300px" : "0px",
    slidesToShow: 1,
    slidesToScroll: 1,
    speed: 500,
    dots: !isSingle,
    dotsClass: "slick-dots main-slider-dots",
    autoplay: !isSingle,
    autoplaySpeed: slideDuration,
    focusOnSelect: false,
    beforeChange: handleBeforeChange,
    afterChange: handleAfterChange,
    responsive: [
      {
        breakpoint: 1500,
        settings: {
          className: "center",
          slidesToShow: 1,
          centerMode: hasSidePeek,
          infinite: !isSingle,
          centerPadding: hasSidePeek ? "150px" : "0px",
          slidesToScroll: 1,
          speed: 500,
          dots: !isSingle,
          dotsClass: "slick-dots main-slider-dots",
          focusOnSelect: false,
        },
      },
      {
        breakpoint: 768,
        settings: {
          className: "slider",
          centerMode: false,
          slidesToShow: 1,
          infinite: !isSingle,
          slidesToScroll: 1,
          speed: 500,
          dots: !isSingle,
          dotsClass: "slick-dots main-slider-dots",
          focusOnSelect: false,
        },
      },
    ],
  };

  const rootClassName = [
    "main-slider",
    isSingle ? "main-slider--single" : "",
    hasManyDots ? "main-slider--compact-dots" : "",
  ]
    .filter(Boolean)
    .join(" ");

  // slick reads centerMode and infinite when it initialises and never again,
  // so the slider is remounted whenever the banner count crosses one of the
  // thresholds that change them
  const sliderKey = `${isSingle ? "single" : "multi"}-${hasSidePeek ? "peek" : "flush"
    }`;

  return (
    <div className={rootClassName} ref={containerRef}>
      <div className="pt-4 slider-custom-style main-slider__inner">
        <Slider ref={sliderRef} key={sliderKey} {...settings}>
          {banners.length ? (
            banners.map((banner) => (
              <div key={banner.id} className="banner-slide-item">
                <BannerSlide
                  banner={banner}
                  isMuted={isMuted}
                  onMuteToggle={handleMuteToggle}
                  onBannerClick={handleBannerClick}
                />
              </div>
            ))
          ) : (
            <div className="banner-slide-item">
              <div className="banner-slide-shell">
                <div className="banner-slide-frame banner-slide-placeholder banner-slide-frame--loading" />
              </div>
            </div>
          )}
        </Slider>
      </div>
    </div>
  );
}
